import React, { useState, useEffect, useRef, useMemo, useCallback, useLayoutEffect } from 'react';
import { type NavigateOptions, type To, useLocation } from 'react-router-dom';
import type { Transaction } from '../types';
import SplitTransactionCard from './SplitTransactionCard';
import DraggableBottomSheet from './DraggableBottomSheet';
import TagSelector from './TagSelector';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { updateTransactionTag } from '../store/slices/transactionsSlice';
import { fetchTagsByType, selectTagMap } from '../store/slices/tagsSlice';
import { useNavigation } from '../hooks/useNavigation';
import { formatMonthYear } from '../utils/dateUtils';
import { getRenderableTransactionRoots } from '../utils/transactionUtils';
import { AutoSizer } from 'react-virtualized-auto-sizer';
import { Virtuoso, type ListRange, type StateSnapshot, type VirtuosoHandle } from 'react-virtuoso';

interface TransactionListProps {
  transactions: Transaction[];
  className?: string;
  topContent?: React.ReactNode;
  useFullWidth?: boolean;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  nestedSheetZIndex?: number;
  requireUserScrollBeforeLoadMore?: boolean;
  getTransactionDetailPath?: (transaction: Transaction) => To;
  getTransactionSplitPath?: (transaction: Transaction) => To;
  getTransactionNavigationState?: (transaction: Transaction) => NavigateOptions['state'];
}

type TransactionListItem =
  | { type: 'top-content'; key: string; content: React.ReactNode }
  | { type: 'header'; key: string; monthYear: string }
  | { type: 'transaction'; key: number; transaction: Transaction }
  | { type: 'footer'; key: string }
  | { type: 'bottom-spacer'; key: string };

interface ItemData {
  items: TransactionListItem[];
  tagMap: ReturnType<typeof selectTagMap>;
  openDetailView: (tx: Transaction) => void;
  openTagSelector: (tx: Transaction, event: React.MouseEvent) => void;
  openSplitView: (tx: Transaction) => void;
  useFullWidth: boolean;
  hasMore?: boolean;
  isLoadingMore?: boolean;
}

const findTransactionById = (transactions: Transaction[], targetId: number): Transaction | null => {
  for (const transaction of transactions) {
    if (transaction.id === targetId) {
      return transaction;
    }

    const subTransaction = transaction.subTransactions?.find((sub) => sub.id === targetId);
    if (subTransaction) {
      return subTransaction;
    }
  }

  return null;
};

type StoredVirtuosoState = {
  totalCount: number;
  snapshot: StateSnapshot;
};

const VIRTUOSO_STATE_LIMIT = 80;
const virtuosoStateByRoute = new Map<string, StoredVirtuosoState>();

const setVirtuosoStateForRoute = (routeKey: string, value: StoredVirtuosoState): void => {
  virtuosoStateByRoute.set(routeKey, value);
  while (virtuosoStateByRoute.size > VIRTUOSO_STATE_LIMIT) {
    const firstKey = virtuosoStateByRoute.keys().next().value;
    if (firstKey === undefined) {
      return;
    }
    virtuosoStateByRoute.delete(firstKey);
  }
};

const renderTransactionItem = (item: TransactionListItem, data: ItemData): React.ReactElement | null => {
  if (!item) {
    return null;
  }

  const rowClassName = item.type === 'bottom-spacer'
    ? ''
    : item.type === 'top-content'
      ? 'pb-1 pt-1'
      : 'px-4 pb-1 pt-1';
  const contentClassName = data.useFullWidth ? 'w-full' : 'mx-auto w-full max-w-sm';

  return (
    <div className={rowClassName}>
      {item.type === 'header' && (
        <div className={contentClassName}>
          <div className="flex justify-between items-center mt-1 mb-1 px-1">
            <h2 className="text-sm font-semibold text-muted-foreground">{item.monthYear}</h2>
          </div>
        </div>
      )}
      {item.type === 'top-content' && (
        <div className="pb-1">
          {item.content}
        </div>
      )}
      {item.type === 'transaction' && (
        <div className={contentClassName}>
          <SplitTransactionCard
            transaction={item.transaction}
            tagMap={data.tagMap}
            openDetailView={data.openDetailView}
            openTagSelector={data.openTagSelector}
            openSplitView={data.openSplitView}
          />
        </div>
      )}
      {item.type === 'footer' && (
        <div className={contentClassName}>
          <div className="flex justify-center items-center h-12 text-muted-foreground text-sm">
            {data.isLoadingMore
              ? 'Loading more transactions...'
              : data.hasMore
                ? ''
                : 'No more transactions.'}
          </div>
        </div>
      )}
      {item.type === 'bottom-spacer' && <div className="h-8" />}
    </div>
  );
};

const TransactionList: React.FC<TransactionListProps> = ({
  transactions,
  className,
  topContent,
  useFullWidth = false,
  onLoadMore,
  hasMore,
  isLoadingMore,
  nestedSheetZIndex = 60,
  requireUserScrollBeforeLoadMore = false,
  getTransactionDetailPath,
  getTransactionSplitPath,
  getTransactionNavigationState,
}) => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { navigateTo } = useNavigation();

  const { debitTags, creditTags, debitStatus, creditStatus } = useAppSelector((state) => state.tags);
  const isTagUpdating = useAppSelector((state) => state.transactions.tagUpdateStatus === 'loading');
  const tagMap = useAppSelector(selectTagMap);

  const [isTagSelectorOpen, setIsTagSelectorOpen] = useState<boolean>(false);
  const [selectedTransactionForTagId, setSelectedTransactionForTagId] = useState<number | null>(null);
  const [listElement, setListElement] = useState<HTMLDivElement | null>(null);
  const virtuosoRef = useRef<VirtuosoHandle | null>(null);

  const loadMoreRequestedRef = useRef<number | null>(null);
  const hasUserScrolledRef = useRef<boolean>(!requireUserScrollBeforeLoadMore);

  const handleScrollerRef = useCallback((element: HTMLElement | Window | null) => {
    const scroller = element instanceof HTMLDivElement ? element : null;
    setListElement(scroller);
  }, []);

  const selectedTransactionForTag = useMemo(() => {
    if (selectedTransactionForTagId == null) {
      return null;
    }
    return findTransactionById(transactions, selectedTransactionForTagId);
  }, [transactions, selectedTransactionForTagId]);

  useEffect(() => {
    if (!requireUserScrollBeforeLoadMore) {
      hasUserScrolledRef.current = true;
      return;
    }

    hasUserScrolledRef.current = false;
    if (!listElement) {
      return;
    }

    const handleScroll = () => {
      if (listElement.scrollTop > 0) {
        hasUserScrolledRef.current = true;
      }
    };

    listElement.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      listElement.removeEventListener('scroll', handleScroll);
    };
  }, [listElement, requireUserScrollBeforeLoadMore]);

  const closeTagSelector = useCallback(() => {
    setIsTagSelectorOpen(false);
    setSelectedTransactionForTagId(null);
  }, []);

  const openTagSelector = useCallback((tx: Transaction, event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedTransactionForTagId(tx.id);
    setIsTagSelectorOpen(true);
  }, []);

  useEffect(() => {
    if (!isTagSelectorOpen || !selectedTransactionForTag) {
      return;
    }

    if (selectedTransactionForTag.type === 'CREDIT' && creditStatus === 'idle') {
      dispatch(fetchTagsByType('CREDIT'));
      return;
    }

    if (selectedTransactionForTag.type === 'DEBIT' && debitStatus === 'idle') {
      dispatch(fetchTagsByType('DEBIT'));
    }
  }, [dispatch, isTagSelectorOpen, selectedTransactionForTag, creditStatus, debitStatus]);

  const handleUpdateTag = useCallback(async (newTag: string | null) => {
    if (isTagUpdating) return;
    if (!selectedTransactionForTag) return;
    dispatch(updateTransactionTag({
      transactionId: selectedTransactionForTag.id,
      newTag,
      originalTransaction: selectedTransactionForTag
    })).then(() => {
    }).catch((error: any) => {
      console.error("Failed to update tag via Redux:", error);
    });
  }, [dispatch, isTagUpdating, selectedTransactionForTag]);

  const openDetailView = useCallback((tx: Transaction) => {
    navigateTo(getTransactionDetailPath?.(tx) ?? `/transactions/${tx.id}`, {
      state: getTransactionNavigationState?.(tx) ?? { transaction: tx }
    });
  }, [getTransactionDetailPath, getTransactionNavigationState, navigateTo]);

  const openSplitView = useCallback((tx: Transaction) => {
    navigateTo(getTransactionSplitPath?.(tx) ?? `/transactions/${tx.id}/split`, {
      state: getTransactionNavigationState?.(tx) ?? { transaction: tx }
    });
  }, [getTransactionNavigationState, getTransactionSplitPath, navigateTo]);

  const listItems = useMemo((): TransactionListItem[] => {
    const items: TransactionListItem[] = [];
    if (topContent) {
      items.push({
        type: 'top-content',
        key: 'top-content',
        content: topContent
      });
    }
    let lastRenderedMonthYear: string | null = null;
    const renderableTransactions = getRenderableTransactionRoots(transactions);
    for (const tx of renderableTransactions) {
      const currentMonthYear = formatMonthYear(tx.transactionDate);
      if (currentMonthYear !== lastRenderedMonthYear) {
        items.push({
          type: 'header',
          key: `header-${currentMonthYear}-${tx.id}`,
          monthYear: currentMonthYear
        });
        lastRenderedMonthYear = currentMonthYear;
      }
      items.push({ type: 'transaction', key: tx.id, transaction: tx });
    }
    if (onLoadMore) {
      items.push({ type: 'footer', key: 'footer' });
    }
    if (items.length > 0) {
      items.push({ type: 'bottom-spacer', key: 'bottom-spacer' });
    }
    return items;
  }, [topContent, transactions, onLoadMore]);
  const routeStateKey = useMemo(
    () => `${location.pathname}${location.search}`,
    [location.pathname, location.search]
  );
  const restoreStateFrom = useMemo(() => {
    const stored = virtuosoStateByRoute.get(routeStateKey);
    if (!stored || stored.totalCount !== listItems.length) {
      return undefined;
    }
    return stored.snapshot;
  }, [routeStateKey, listItems.length]);

  useEffect(() => {
    if (loadMoreRequestedRef.current !== null && listItems.length > loadMoreRequestedRef.current) {
      loadMoreRequestedRef.current = null;
    }
  }, [listItems.length]);

  useEffect(() => {
    if (!isLoadingMore) {
      loadMoreRequestedRef.current = null;
    }
  }, [isLoadingMore]);

  const persistVirtuosoState = useCallback(() => {
    const virtuoso = virtuosoRef.current;
    if (!virtuoso || listItems.length === 0) {
      return;
    }

    virtuoso.getState((snapshot) => {
      setVirtuosoStateForRoute(routeStateKey, {
        totalCount: listItems.length,
        snapshot
      });
    });
  }, [routeStateKey, listItems.length]);

  useLayoutEffect(() => {
    return () => {
      persistVirtuosoState();
    };
  }, [persistVirtuosoState]);

  const handleRangeChanged = useCallback((range: ListRange) => {
    if (!onLoadMore || !hasMore || isLoadingMore) return;
    if (requireUserScrollBeforeLoadMore && !hasUserScrolledRef.current) return;
    const lastDataIndex = listItems.length - 1;
    if (lastDataIndex < 0) return;
    const triggerIndex = Math.max(0, lastDataIndex - 5);
    if (range.endIndex >= triggerIndex) {
      if (loadMoreRequestedRef.current === listItems.length) return;
      loadMoreRequestedRef.current = listItems.length;
      onLoadMore();
    }
  }, [onLoadMore, hasMore, isLoadingMore, listItems.length, requireUserScrollBeforeLoadMore]);

  const itemData = useMemo<ItemData>(() => ({
    items: listItems,
    tagMap,
    openDetailView,
    openTagSelector,
    openSplitView,
    useFullWidth,
    hasMore,
    isLoadingMore
  }), [listItems, tagMap, openDetailView, openTagSelector, openSplitView, useFullWidth, hasMore, isLoadingMore]);
  const itemKey = useCallback((index: number): string | number => listItems[index]?.key ?? index, [listItems]);
  const itemContent = useCallback((index: number) => {
    const item = listItems[index];
    if (!item) {
      return null;
    }
    return renderTransactionItem(item, itemData);
  }, [listItems, itemData]);

  return (
    <div
      className={`relative ${className ?? ''}`}
      style={{ scrollbarWidth: 'none', height: '100%' }}
    >
      {listItems.length > 0 && (
        <AutoSizer
          renderProp={({ height, width }) => {
            if (height == null || width == null) {
              return null;
            }
            return (
              <Virtuoso
                ref={virtuosoRef}
                totalCount={listItems.length}
                computeItemKey={itemKey}
                itemContent={itemContent}
                rangeChanged={handleRangeChanged}
                restoreStateFrom={restoreStateFrom}
                scrollerRef={handleScrollerRef}
                increaseViewportBy={600}
                style={{ height, width, scrollbarWidth: 'none' }}
              />
            );
          }}
        />
      )}

      <DraggableBottomSheet
        isOpen={isTagSelectorOpen}
        onClose={closeTagSelector}
        title="Tag Transaction"
        zIndex={nestedSheetZIndex}
        closeLoading={isTagUpdating}
      >
        <TagSelector
          onSelectTag={handleUpdateTag}
          availableTags={selectedTransactionForTag?.type === 'CREDIT' ? creditTags : debitTags}
          tagMap={tagMap}
          currentTagName={selectedTransactionForTag?.tag}
          transaction={selectedTransactionForTag ?? undefined}
          disabled={isTagUpdating}
        />
      </DraggableBottomSheet>
    </div>
  );
};

export default TransactionList; 
